import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Container,
    Table,
    Button,
    Row,
    Col,
    Pagination,
    PaginationItem,
    PaginationLink,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEdit, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import BaseUrl from "../../base-api";
import moment from 'moment/moment';
import jsPDF from 'jspdf';
import logo from "./../../assets/logo.jpg";
import hkcl from "../../assets/hkcl.png";
import imageCompression from 'browser-image-compression';

import 'jspdf-autotable';

const SessionForm = () => {
    const [sessions, setSessions] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [size] = useState(10);
    const [modal, setModal] = useState(false);
    const [sessionName, setSessionName] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [editSession, setEditSession] = useState({});
    const [latestSessionKey, setLatestSessionKey] = useState("");
    const [exportingPdf, setExportingPdf] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);

    const toggleModal = () => setModal(!modal);

    const fetchSessions = async () => {
        try {
            const response = await axios.get(`${BaseUrl}/admin/getAllSessions`, {
                params: { page: currentPage, size },
                headers: {
                    Authorization: localStorage.getItem("$3ingT0k3n"),
                },
            });

            const sessionList = response.data.response;
            setSessions(sessionList);
            setTotalPages(response.data.totalPages);

            // Find session with latest toDate
            if (sessionList.length > 0) {
                const latest = sessionList.reduce((a, b) =>
                    new Date(b.toDate) > new Date(a.toDate) ? b : a
                );
                setLatestSessionKey(latest.sessionKey);
            }

        } catch (error) {
            toast.error("Error fetching sessions");
        }
    };

    const exportData = async (sessionKey) => {
        setExportingExcel(sessionKey);
        try {
            const response = await axios.get(`${BaseUrl}/admin/export-excel`, {
                headers: {
                    Authorization: localStorage.getItem("$3ingT0k3n"),
                },
                responseType: "blob",
                params: { sessionKey },
            });

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Session_Report.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error("Excel export failed");
        } finally {
            setExportingExcel(false);
        }
    };

    const compressImage = async (base64Image) => {
        const response = await fetch(base64Image);
        const blob = await response.blob();
        const options = {
            maxSizeMB: 2,
            maxWidthOrHeight: 1200,
            useWebWorker: true,
        };
        try {
            const compressedBlob = await imageCompression(blob, options);
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(compressedBlob);
                reader.onloadend = () => resolve(reader.result);
            });
        } catch (error) {
            console.error("Image compression error:", error);
            return base64Image;
        }
    };

   
    const exportPdf = async (sessionKey) => {
        setExportingPdf(sessionKey);
        try {
            const response = await axios.get(`${BaseUrl}/admin/get-session-application-data`, {
                headers: {
                    Authorization: localStorage.getItem("$3ingT0k3n"),
                },
                params: { sessionKey },
            });

            const dataList = response.data;
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();

            const websiteName = "Guru Jambheshwar University of Science & Technology, Hisar";
            const websiteNameWork = "Admission Form";

            const currentDate = new Date();
            const currentTime = currentDate.toLocaleString('en-GB', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });

            const imageWidth = 20;
            const imageHeight = 20;
            const imageX = 10;

            const addHeader = () => {
                doc.setFontSize(8).setTextColor("black");
                const generatedTextWidth = doc.getTextWidth(`Generated on: ${currentTime}`);
                const generatedX = pageWidth - generatedTextWidth - 10;
                doc.text(`Generated on: ${currentTime}`, generatedX, 10);
                doc.text("https://gjuskillcourses.hkcl.in", 10, 10);
            };

            // Ensure logo and hkcl are defined or imported
            // For demonstration, let's assume 'logo' and 'hkcl' are base64 strings or properly imported
            const logo = "data:image/jpeg;base64,..."; // Replace with actual base64 logo
            const hkcl = "data:image/jpeg;base64,..."; // Replace with actual base64 hkcl image


            for (let i = 0; i < dataList.length; i++) {
                const course = dataList[i];

                let photo = null;
                let sign = null;

                // Process documents for photo and signature
                if (course.documents && Array.isArray(course.documents)) {
                    for (const docItem of course.documents) {
                        if (docItem.data) {
                            try {
                                const base64Image = 'data:image/jpeg;base64,' + docItem.data; // Assuming JPEG, adjust if needed
                                const compressed = await compressImage(base64Image); // Make sure compressImage is defined
                                if (docItem.docName === "Photograph") photo = compressed;
                                if (docItem.docName === "Signature") sign = compressed;
                            } catch (imgErr) {
                                console.warn(`Error processing image for ${docItem.docName}:`, imgErr);
                                // If compression fails, we'll just leave photo/sign as null
                            }
                        }
                    }
                }

                doc.setFont("helvetica");
                doc.setFontSize(16).setFont("helvetica", "bold");
                addHeader();

                // Add Logo (handle if logo is missing/invalid)
                try {
                    if (logo) doc.addImage(logo, 'JPEG', imageX, 12, imageWidth, imageHeight);
                } catch (err) {
                    console.warn("Error adding logo:", err);
                }

                doc.setFontSize(12).setTextColor("green");
                doc.text(websiteName, imageX + imageWidth + 12, 20);
                doc.text(websiteNameWork, (pageWidth / 2) - imageX - imageWidth + 15, 30);

                // Add HKCL image (handle if hkcl is missing/invalid)
                try {
                    if (hkcl) doc.addImage(hkcl, 'JPEG', pageWidth - imageX - imageWidth - 5, 14, imageWidth + 5, imageHeight);
                } catch (err) {
                    console.warn("Error adding HKCL image:", err);
                }


                doc.setFontSize(12).setTextColor("green");
                doc.text("Personal Details", 15, 50);

                doc.autoTable({
                    startY: 55,
                    columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 100 } },
                    body: [
                        [{ content: "Candidate Name", styles: { fontStyle: 'bold' } }, course.candidateName || 'N/A'],
                        [{ content: "Father Name", styles: { fontStyle: 'bold' } }, course.fatherName || 'N/A'],
                        [{ content: "Mobile Number", styles: { fontStyle: 'bold' } }, course.mobileNumber || 'N/A'],
                        [{ content: "Email", styles: { fontStyle: 'bold' } }, course.email || 'N/A'],
                        [{ content: "Date of Birth", styles: { fontStyle: 'bold' } }, course.dateOfBirth ? moment(course.dateOfBirth).format("DD/MM/YYYY") : 'N/A']
                    ],
                    theme: 'grid',
                    styles: { lineWidth: 0.1, lineColor: [0, 0, 0] },
                    didDrawPage: (data) => {
                        if (data.pageNumber > 1) addHeader();
                        // Photo placeholder logic
                        if (photo) {
                            doc.addImage(photo, 'JPEG', pageWidth - 50, 55, 35, 38);
                        } else {
                            // Draw an empty box if photo is missing
                            doc.rect(pageWidth - 50, 55, 35, 38);
                            doc.setFontSize(8);
                            doc.text("Photo Missing", pageWidth - 50 + 35 / 2, 55 + 38 / 2, { align: 'center' });
                        }
                    }
                });

                const label = "Enrolled Course: ";
                const courseName = Array.isArray(course.courseName)
                    ? (course.courseName.length > 0 ? course.courseName.join(", ") : 'N/A')
                    : (course.courseName || 'N/A');

                doc.setFontSize(12).setTextColor("green");
                doc.text(label, 15, doc.autoTable.previous.finalY + 10); // Adjust Y position based on previous table
                const labelWidth = doc.getTextWidth(label);
                doc.setTextColor("black");
                doc.text(courseName, 15 + labelWidth, doc.autoTable.previous.finalY + 10); // Adjust Y position


                const twelfthTitleY = doc.autoTable.previous.finalY + 25;
                const twelfthTableStartY = twelfthTitleY + 10;

                doc.setFontSize(12).setTextColor("green");
                doc.text("Details of 12th Standard or Equivalent", 15, twelfthTitleY);

                doc.autoTable({
                    startY: twelfthTableStartY,
                    body: [
                        [{ content: "Roll Number", styles: { fontStyle: 'bold' } }, course.rollNumber || 'N/A'],
                        [{ content: "Board", styles: { fontStyle: 'bold' } }, course.board || 'N/A'],
                        [{ content: "Passing Month-Year", styles: { fontStyle: 'bold' } }, course.passingMonthYear || 'N/A'],
                        [{ content: "Marks Obtained", styles: { fontStyle: 'bold' } }, course.obtainedMarks || 'N/A']
                    ],
                    theme: 'grid',
                    styles: {
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        minCellHeight: 12,
                        fontSize: 10,
                        valign: 'middle'
                    },
                    didDrawPage: (data) => {
                        if (data.pageNumber > 1) addHeader();
                    }
                });

                doc.setFontSize(12).setTextColor("green");
                const extraSpacing = 20;
                const addressTitleY = doc.autoTable.previous.finalY + extraSpacing;
                const addressTableStartY = addressTitleY + 10;

                doc.text("Address Details", 15, addressTitleY);

                doc.autoTable({
                    startY: addressTableStartY,
                    body: [
                        [{ content: "Address", styles: { fontStyle: 'bold' } }, course.address || 'N/A'],
                        [{ content: "District", styles: { fontStyle: 'bold' } }, course.district || 'N/A'],
                        [{ content: "State", styles: { fontStyle: 'bold' } }, course.state || 'N/A'],
                        [{ content: "Country", styles: { fontStyle: 'bold' } }, course.country || 'N/A'],
                        [{ content: "Pincode", styles: { fontStyle: 'bold' } }, course.pincode || 'N/A']
                    ],
                    theme: 'grid',
                    styles: {
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        minCellHeight: 12,
                        fontSize: 10,
                        valign: 'middle'
                    },
                    didDrawPage: (data) => {
                        if (data.pageNumber > 1) addHeader();
                    }
                });

                doc.addPage();
                doc.text("Submitted Documents", 15, 20);

                doc.autoTable({
                    startY: 30,
                    head: [['Sr. No.', 'Document Name', 'Preview']],
                    body: (course.documents || []).map((item, index) => [
                        index + 1,
                        item.docName || 'Unknown Document', // Handle missing docName
                        ''
                    ]),
                    theme: 'grid',
                    styles: {
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        halign: 'center',
                        valign: 'middle',
                        fontSize: 10,
                        minCellHeight: 30
                    },
                    columnStyles: {
                        0: { cellWidth: 25 },
                        1: { cellWidth: 70 },
                        2: { cellWidth: 50 }
                    },
                    didDrawCell: (data) => {
                        const rowIndex = data.row.index;
                        const colIndex = data.column.index;

                        if (
                            data.section === 'body' &&
                            colIndex === 2
                        ) {
                            const docItem = course.documents && course.documents[rowIndex];
                            if (docItem && docItem.data) {
                                let base64 = docItem.data;
                                if (!base64.startsWith('data:image')) {
                                    base64 = 'data:image/jpeg;base64,' + base64; // Default to JPEG if no prefix
                                }

                                const format = base64.startsWith('data:image/png') ? 'PNG' : 'JPEG';
                                const imgMaxWidth = 40;
                                const imgMaxHeight = 25;

                                try {
                                    const props = doc.getImageProperties(base64);
                                    const aspectRatio = props.width / props.height;

                                    let imgWidth = imgMaxWidth;
                                    let imgHeight = imgMaxWidth / aspectRatio;

                                    if (imgHeight > imgMaxHeight) {
                                        imgHeight = imgMaxHeight;
                                        imgWidth = aspectRatio * imgMaxHeight;
                                    }

                                    const x = data.cell.x + (data.cell.width - imgWidth) / 2;
                                    const y = data.cell.y + (data.cell.height - imgHeight) / 2;

                                    doc.addImage(base64, format, x, y, imgWidth, imgHeight);
                                } catch (err) {
                                    console.warn("Image rendering error for document:", err);
                                    // Draw an empty box and text if image rendering fails
                                    doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height);
                                    doc.setFontSize(7);
                                    doc.text("Image N/A", data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, { align: 'center' });
                                }
                            } else {
                                // Draw an empty box and text if docItem or docItem.data is missing
                                doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height);
                                doc.setFontSize(7);
                                doc.text("Document N/A", data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, { align: 'center' });
                            }
                        }
                    }
                });

                // Signature
                const paragraphText = "I declare that the information provided by me in the application form is true and correct to the best of my knowledge, and I have not concealed any facts. I also confirm that I have carefully read all the guidelines and instructions provided on the admission portal before submitting my application. I also acknowledge the importance of providing correct contact details, as all course-related updates will be sent to my registered mobile number and email ID. I am fully aware that the course once selected cannot be changed under any circumstances and that the course fee, once paid, is non-refundable and non-transferable. I also give my consent to receive updates and course-related information on my registered mobile number/WhatsApp and email ID.";

                const textStartY = doc.autoTable.previous.finalY + 10;
                const signatureStartY = textStartY + 30;

                doc.setFontSize(9);
                const lines = doc.splitTextToSize(paragraphText, 180);
                doc.text(lines, 15, textStartY, { maxWidth: 180, align: 'justify' });

                doc.setFontSize(8).setFont("helvetica", "underline").setTextColor("black");

                // Signature placeholder logic
                if (sign) {
                    doc.addImage(sign, 'JPEG', pageWidth - 50, signatureStartY, 35, 10);
                } else {
                    // Draw an empty box if signature is missing
                    doc.rect(pageWidth - 50, signatureStartY, 35, 10);
                    doc.setFontSize(8);
                    doc.text("Signature Missing", pageWidth - 50 + 35 / 2, signatureStartY + 10 / 2, { align: 'center' });
                }
                doc.text("(Applicant Signature)", pageWidth - 40, signatureStartY + 15);

                if (i < dataList.length - 1) doc.addPage();
            }

            doc.save('All_Applications_Report.pdf');
        } catch (error) {
            console.error('PDF generation failed:', error);
            toast.error('Failed to generate PDF.');
        } finally {
            setExportingPdf(false);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleEdit = (session) => {
        setEditSession(session);
        toggleModal();
    };

    const handleUpdate = async () => {
        try {
            const response = await axios.put(
                `${BaseUrl}/admin/updateSession`,
                editSession,
                {
                    headers: {
                        Authorization: localStorage.getItem("$3ingT0k3n"),
                    },
                }
            );
            if (response.status === 200) {
                toast.success("Session updated");
                fetchSessions();
                toggleModal();
            }
        } catch (error) {
            toast.error("Failed to update session");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { sessionName, fromDate, toDate };
            const response = await axios.post(`${BaseUrl}/admin/saveSession`, payload, {
                headers: {
                    Authorization: localStorage.getItem("$3ingT0k3n"),
                },
            });

            if (response.status === 200) {
                toast.success("Session saved");
                setSessionName("");
                setFromDate("");
                setToDate("");
                fetchSessions();
            }
        } catch (error) {
            toast.error("Failed to save session");
        }
    };

    useEffect(() => {
        fetchSessions();
    }, [currentPage]);

    return (
        <Container className="mt-4">
            <h4 className="text-center mb-4">Session Management</h4>

            <Table bordered hover>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Session Name</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sessions.length > 0 ? (
                        sessions.map((s, index) => (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{s.sessionName}</td>
                                <td>{s.fromDate}</td>
                                <td>{s.toDate}</td>
                                <td>
                                    <Button
                                        size="sm"
                                        color="success"
                                        onClick={() => exportData(s.sessionKey)}
                                        disabled={exportingExcel === s.sessionKey}
                                    >
                                        {exportingExcel === s.sessionKey ? 'Exporting...' : (
                                            <><FontAwesomeIcon icon={faDownload} /> Export</>
                                        )}
                                    </Button>{" "}
                                    <Button
                                        size="sm"
                                        color="danger"
                                        onClick={() => exportPdf(s.sessionKey)}
                                        disabled={exportingPdf === s.sessionKey}
                                    >
                                        {exportingPdf === s.sessionKey ? 'Generating...' : (
                                            <><FontAwesomeIcon icon={faFilePdf} /> PDF</>
                                        )}
                                    </Button>{" "}
                                    {s.sessionKey === latestSessionKey && (
                                        <Button
                                            size="sm"
                                            color="info"
                                            onClick={() => handleEdit(s)}
                                        >
                                            <FontAwesomeIcon icon={faEdit} /> Edit
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="5" className="text-center">
                                No sessions found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            <Row className="justify-content-center mt-4">
                <Col md="auto">
                    <Pagination>
                        <PaginationItem disabled={currentPage === 0}>
                            <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                        </PaginationItem>
                        <PaginationItem>
                            <PaginationLink disabled>
                                Page {currentPage + 1} of {totalPages}
                            </PaginationLink>
                        </PaginationItem>
                        <PaginationItem disabled={currentPage >= totalPages - 1}>
                            <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                        </PaginationItem>
                    </Pagination>
                </Col>
            </Row>

            <h5 className="mt-5 text-center">Create New Session</h5>
            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label>Session Name</Label>
                            <Input value={sessionName} onChange={(e) => setSessionName(e.target.value)} required />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label>From Date</Label>
                            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} required />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label>To Date</Label>
                            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} required />
                        </FormGroup>
                    </Col>
                </Row>
                <div className="text-center">
                    <Button color="primary" type="submit">
                        Save Session
                    </Button>
                </div>
            </Form>

            <Modal isOpen={modal} toggle={toggleModal} centered>
                <ModalHeader toggle={toggleModal}>Edit Session</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Session Name</Label>
                            <Input
                                value={editSession.sessionName || ""}
                                onChange={(e) =>
                                    setEditSession({ ...editSession, sessionName: e.target.value })
                                }
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>From Date</Label>
                            <Input type="date" value={editSession.fromDate || ""} disabled />
                        </FormGroup>
                        <FormGroup>
                            <Label>To Date</Label>
                            <Input
                                type="date"
                                value={editSession.toDate || ""}
                                onChange={(e) =>
                                    setEditSession({ ...editSession, toDate: e.target.value })
                                }
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleUpdate}>
                        Update
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default SessionForm;
